import {
  BLUR_EDGE,
  IMAGE_JPEG_QUALITY,
  IMAGE_KEEP_AS_IS_BYTES,
  IMAGE_MAX_EDGE,
  SMALL_EDGE,
  SMALL_JPEG_QUALITY,
  SMALL_WORTH_RATIO,
  isBlur,
} from '../../../shared/constants';
import { withoutExif } from './jpeg';

export interface PreparedImage {
  blob: Blob;
  width: number;
  height: number;
}

/**
 * Re-encodes a camera photo to a sane JPEG.
 *
 * Going through a canvas is also what strips EXIF, GPS coordinates included:
 * the pixels are copied, the metadata is not. That is a privacy requirement,
 * not just a size optimization.
 *
 * Every step here has more than one way of being done, and each one is tried
 * until one works rather than picked once by feature detection. Detection
 * answers "does this exist", and the question that matters on an older phone
 * is "does this work": `createImageBitmap` has existed in Safari for years
 * while refusing the options dictionary passed to it until 17, so a phone that
 * passed the test still threw — and threw before any of this reached the
 * network, which is why the failure left no trace on the server at all.
 */
export async function prepareImage(file: File): Promise<PreparedImage> {
  /*
   * The camera's own file, wherever that can be handed over safely.
   *
   * Redrawing strips the metadata but takes the photograph with it: the gain
   * map that makes a Pixel's picture look the way it did in the viewfinder
   * cannot survive a 2D canvas, and Display P3 comes out the far side as
   * sRGB. So a plain JPEG has its EXIF emptied in place and is sent as it
   * was, and everything else — a HEIC, a JPEG whose XMP carries coordinates,
   * or one too big to go up quickly — still goes the long way round.
   */
  if (file.size <= IMAGE_KEEP_AS_IS_BYTES) {
    const kept = await withoutExif(file);
    if (kept) return { blob: kept.blob, width: kept.width, height: kept.height };
  }

  const bitmap = await loadBitmap(file);
  const { width, height } = fit(sizeOf(bitmap).width, sizeOf(bitmap).height, IMAGE_MAX_EDGE);

  try {
    const blob = await draw(bitmap, width, height);
    return { blob, width, height };
  } finally {
    if ('close' in bitmap) bitmap.close();
  }
}

/**
 * The photo's blurred stand-in: the picture as it is being sent, drawn at
 * BLUR_EDGE on its long side and kept as a data URL on its ref, for Photo to
 * show while the photo itself downloads.
 *
 * Asked to decode straight down to that size first, which is far less work
 * than a 12-megapixel decode; a Safari before 17 refuses the size and decodes
 * the whole thing, which still works, only slower.
 */
export async function blurOf(image: Blob, width: number, height: number): Promise<string> {
  const small = fit(width, height, BLUR_EDGE);
  let bitmap: ImageBitmap | HTMLImageElement;
  try {
    bitmap = await createImageBitmap(image, { resizeWidth: small.width, resizeHeight: small.height });
  } catch {
    bitmap = await loadBitmap(image);
  }
  try {
    const canvas = document.createElement('canvas');
    canvas.width = small.width;
    canvas.height = small.height;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('no 2d context');
    ctx.drawImage(bitmap, 0, 0, small.width, small.height);
    const blur = canvas.toDataURL('image/png');
    if (!isBlur(blur)) throw new Error('not a small PNG');
    return blur;
  } finally {
    if ('close' in bitmap) bitmap.close();
  }
}

/**
 * The photo's small copy (SMALL_EDGE on its long side), drawn wherever the
 * photo is not opened on its own. Null when the photo is already near that
 * size, where a copy would save nothing worth the second request.
 */
export async function smallOf(image: Blob, width: number, height: number): Promise<Blob | null> {
  if (Math.max(width, height) < SMALL_EDGE * SMALL_WORTH_RATIO) return null;
  const small = fit(width, height, SMALL_EDGE);
  let bitmap: ImageBitmap | HTMLImageElement;
  try {
    bitmap = await createImageBitmap(image, {
      resizeWidth: small.width,
      resizeHeight: small.height,
      resizeQuality: 'high',
    });
  } catch {
    bitmap = await loadBitmap(image);
  }
  try {
    return await draw(bitmap, small.width, small.height, SMALL_JPEG_QUALITY);
  } finally {
    if ('close' in bitmap) bitmap.close();
  }
}

/** An ImageBitmap and an <img> disagree about which pair of numbers is real. */
function sizeOf(source: ImageBitmap | HTMLImageElement): {
  width: number;
  height: number;
} {
  return source instanceof HTMLImageElement
    ? { width: source.naturalWidth, height: source.naturalHeight }
    : { width: source.width, height: source.height };
}

function fit(
  width: number,
  height: number,
  maxEdge: number,
): { width: number; height: number } {
  const longest = Math.max(width, height);
  if (longest <= maxEdge) return { width, height };
  const scale = maxEdge / longest;
  return { width: Math.round(width * scale), height: Math.round(height * scale) };
}

/**
 * Three ways of turning a file into something drawable, in order of how much
 * they do for us.
 *
 * The first honours the EXIF rotation before the canvas throws EXIF away. The
 * second is the same call without the argument older Safari will not take. The
 * last is an <img>, which is the oldest way and the one that can also decode
 * an HEIC an iPhone handed over unconverted, because the decoding is the
 * system's rather than the canvas's.
 */
async function loadBitmap(file: Blob): Promise<ImageBitmap | HTMLImageElement> {
  if ('createImageBitmap' in window) {
    try {
      return await createImageBitmap(file, { imageOrientation: 'from-image' });
    } catch {
      try {
        return await createImageBitmap(file);
      } catch {
        // Both refused. The element below may still manage it.
      }
    }
  }

  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    // Either way the blob URL is finished with; leaving it holds the whole
    // file in memory for as long as the page lives.
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('That photo could not be read on this phone.'));
    };
    img.src = url;
  });
}

/** Offscreen if it will, on an element if it will not. */
async function draw(
  source: ImageBitmap | HTMLImageElement,
  width: number,
  height: number,
  quality = IMAGE_JPEG_QUALITY,
): Promise<Blob> {
  if ('OffscreenCanvas' in window) {
    try {
      const canvas = new OffscreenCanvas(width, height);
      const ctx = canvas.getContext('2d');
      if (!ctx) throw new Error('no 2d context');
      ctx.drawImage(source, 0, 0, width, height);
      return await canvas.convertToBlob({ type: 'image/jpeg', quality });
    } catch {
      // Present but unwilling — convertToBlob is newer than the constructor.
    }
  }

  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('This phone would not open a canvas to resize the photo.');
  ctx.drawImage(source, 0, 0, width, height);

  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) =>
        blob
          ? resolve(blob)
          : reject(new Error('This phone could not re-encode the photo.')),
      'image/jpeg',
      quality,
    );
  });
}
